import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import { Avatar, StatusBadge } from "./ui";
import { NO_DATA, fmtPhone, fullName, initialsOf, toMs } from "./leadsKit";
import { toLead } from "./contactsKit";
import { InlineLoader } from "../PageLoader";

/**
 * Search-and-pick an existing contact (design: components/crm/ContactPicker.tsx).
 *
 * Props (the design's):
 *   contacts — [{ id, firstName, lastName, phone, email, createdAt }]  (see toPickerContact)
 *   leads    — [{ contactId, createdAt, status }] (status = design status name), or null.
 *              Counts come from the contact's own lead_count and latest_lead
 *              (GET /api/contacts, item 154) when the row has them; `leads` is
 *              only the fallback, and with neither the count shows "—".
 *   onSelect(contact), onCreateNew(query)
 */

/** GET /api/contacts row → the picker's contact. */
export const toPickerContact = (c) => ({
  id: c.id,
  firstName: c.first_name || "",
  lastName: c.last_name || "",
  phone: c.phone || "",
  email: c.email || "",
  altPhone: c.alternate_phone || "",
  title: c.title || "",
  createdAt: toMs(c.created_at) || 0,
  // The list's lead summary (item 154); a single GET /api/contacts/:id has none.
  leadCount: c.lead_count === undefined || c.lead_count === null ? null : Number(c.lead_count) || 0,
  latestLeadStatus: c.latest_lead ? toLead(c.latest_lead).status : null,
});

export default function ContactPicker({ contacts, leads, onSelect, onCreateNew, loading = false }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [popoverStyle, setPopoverStyle] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const popoverRef = useRef(null);

  const sortedContacts = [...contacts].sort((a, b) => b.createdAt - a.createdAt);
  const normalizedQuery = query.trim().toLowerCase();
  const matchingContacts = normalizedQuery
    ? sortedContacts
        .filter((contact) => {
          const rawPhoneQuery = normalizedQuery.replace(/\D/g, "");
          const phoneQuery = rawPhoneQuery.length === 12 && rawPhoneQuery.startsWith("91") ? rawPhoneQuery.slice(2) : rawPhoneQuery;
          return fullName(contact).toLowerCase().includes(normalizedQuery) || (phoneQuery !== "" && String(contact.phone).includes(phoneQuery)) || String(contact.email).toLowerCase().includes(normalizedQuery);
        })
        .slice(0, 8)
    : sortedContacts.slice(0, 6);
  const leadCountByContact = new Map();
  const latestLeadByContact = new Map();
  for (const lead of leads || []) {
    leadCountByContact.set(lead.contactId, (leadCountByContact.get(lead.contactId) ?? 0) + 1);
    const latest = latestLeadByContact.get(lead.contactId);
    if (!latest || lead.createdAt > latest.createdAt) latestLeadByContact.set(lead.contactId, lead);
  }

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) setPopoverStyle({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    };
    const onPointerDown = (event) => {
      const target = event.target;
      if (!rootRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const pick = (contact) => {
    onSelect(contact);
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(0, matchingContacts.length - 1)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && open) {
      event.preventDefault();
      const contact = matchingContacts[activeIndex];
      if (contact) pick(contact);
      else if (normalizedQuery) onCreateNew(query.trim());
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative col-span-2">
      <label className="flex h-11 items-center gap-2 rounded-sm border border-line bg-paper px-3.5 focus-within:border-ink focus-within:ring-2 focus-within:ring-ink/10">
        <Search size={16} strokeWidth={1.75} className="shrink-0 text-muted" />
        <input
          ref={inputRef}
          role="combobox"
          aria-label="Search contacts"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="contact-search-results"
          aria-activedescendant={open && matchingContacts[activeIndex] ? `contact-option-${matchingContacts[activeIndex].id}` : undefined}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search by name, phone or email"
          className="h-full min-w-0 flex-1 bg-transparent text-small text-ink outline-none placeholder:text-muted/70"
        />
      </label>
      {open &&
        createPortal(
          <div ref={popoverRef} style={{ top: popoverStyle.top, left: popoverStyle.left, width: popoverStyle.width }} className="olli fixed z-40 max-h-96 animate-nav-in overflow-y-auto rounded-md bg-paper p-1.5 font-sans text-ink shadow-md ring-1 ring-line">
            <ul id="contact-search-results" role="listbox" aria-label="Contacts">
              {!normalizedQuery && <li className="px-3 pb-2 pt-1 text-caption font-semibold text-muted">Recent contacts</li>}
              {matchingContacts.map((contact, index) => {
                const known = contact.leadCount !== null && contact.leadCount !== undefined;
                const count = known ? contact.leadCount : leadCountByContact.get(contact.id) ?? 0;
                const latestStatus = known ? contact.latestLeadStatus : latestLeadByContact.get(contact.id)?.status;
                return (
                  <li key={contact.id}>
                    <button
                      id={`contact-option-${contact.id}`}
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => pick(contact)}
                      className={`flex w-full items-center gap-3 rounded-xs px-2.5 py-2 text-left ${index === activeIndex ? "bg-mist" : "hover:bg-mist"}`}
                    >
                      <Avatar name={fullName(contact)} initials={initialsOf(contact)} size="sm" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-small font-semibold text-ink">{fullName(contact)}</span>
                        <span className="mt-0.5 block truncate text-caption text-muted">
                          {fmtPhone(contact.phone)}
                          {contact.email ? ` · ${contact.email}` : ""}
                        </span>
                      </span>
                      <span className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-caption text-muted">{leads == null && !known ? NO_DATA : count ? `${count} ${count === 1 ? "lead" : "leads"}` : "No leads"}</span>
                        {latestStatus && <StatusBadge status={latestStatus} className="h-5 px-2 text-caption" />}
                      </span>
                    </button>
                  </li>
                );
              })}
              {matchingContacts.length === 0 && loading && <li className="flex justify-center px-3 py-4"><InlineLoader /></li>}
              {matchingContacts.length === 0 && !loading && normalizedQuery && (
                <li className="flex flex-col gap-2 px-3 py-3">
                  <p className="text-small text-muted">No contact matches “{query.trim()}”</p>
                  <button type="button" onClick={() => onCreateNew(query.trim())} className="w-fit text-small font-semibold text-sky-deep underline hover:text-ink">
                    Create new contact
                  </button>
                </li>
              )}
              {matchingContacts.length === 0 && !loading && !normalizedQuery && <li className="px-3 py-3 text-small text-muted">No contacts yet.</li>}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}
